import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AppBar,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FetchedComponent } from "../components/fetchers/FetchedComponent";
import { Flex } from "../components/Flex";
import {
  getFantasyProjections,
  ingestFantasyData,
} from "../api/FantasyApi";
import {
  anyIsError,
  anyIsSuccess,
  errorMessage,
  useFetchedResource,
} from "../utils/fetchers";
import { DocTitle } from "../utils/useDocTitleEffect";
import {
  GetFantasyProjectionsRequest,
  PlayerProjection,
  StartSitRecommendation,
  WaiverRecommendation,
} from "../models/functions";

const useStyles = makeStyles({
  page: {
    minHeight: "100vh",
    background: "#f4f6f8",
  },
  body: {
    padding: 16,
    maxWidth: 1200,
    margin: "0 auto",
    width: "100%",
    boxSizing: "border-box",
  },
  card: {
    padding: 16,
    marginBottom: 16,
  },
  tableWrap: {
    overflowX: "auto",
  },
});

const slotLabel = (slot?: number): string => {
  const map: { [k: number]: string } = {
    0: "QB",
    2: "RB",
    4: "WR",
    6: "TE",
    16: "D/ST",
    17: "K",
    20: "BE",
    23: "FLEX",
  };
  return slot === undefined ? "" : map[slot] || String(slot);
};

const pts = (n?: number | null) => (n == null ? "—" : n.toFixed(1));

const actionColor = (action: StartSitRecommendation["action"]) => {
  if (action === "start") return "success";
  if (action === "bench") return "warning";
  return "default";
};

const FactorList = ({ player }: { player: PlayerProjection }) => (
  <Flex flexDirection="column" style={{ gap: 6 }}>
    <Typography variant="body2">
      Volume / team total: {player.factors.teamVolume.toFixed(2)} · Opponent:{" "}
      {player.factors.opponentDefense.toFixed(2)} · Home/away:{" "}
      {player.factors.homeAway.toFixed(2)} · Rest: {player.factors.rest.toFixed(2)}{" "}
      · Weather: {player.factors.weather.toFixed(2)} · Teammates:{" "}
      {player.factors.teammateRedistribution.toFixed(2)} · Availability:{" "}
      {player.factors.availability.toFixed(2)}
    </Typography>
    {player.factors.notes.map((note) => (
      <Typography key={note} variant="caption" color="textSecondary">
        {note}
      </Typography>
    ))}
    <Typography variant="caption">
      Line: {player.stats.receptions.toFixed(1)} rec /{" "}
      {player.stats.recYards.toFixed(0)} rec yds / {player.stats.recTds.toFixed(2)}{" "}
      rec TD · {player.stats.rushYards.toFixed(0)} rush yds ·{" "}
      {player.stats.passYards.toFixed(0)} pass yds
    </Typography>
    {(player.heuristicPoints != null || player.consensusPoints != null) && (
      <Typography variant="caption">
        Heuristic {pts(player.heuristicPoints)}
        {player.consensusPoints != null ?
          ` · Consensus ${pts(player.consensusPoints)}` :
          ""}
        {player.consensusAdjustment != null ?
          ` · ${player.consensusAdjustment.toFixed(2)}x` :
          ""}
      </Typography>
    )}
    {player.sourcePoints && (
      <Typography variant="caption">
        ESPN {pts(player.sourcePoints.espn)} · Rotowire{" "}
        {pts(player.sourcePoints.rotowire)} · FantasyPros{" "}
        {pts(player.sourcePoints.fantasypros)} · Props{" "}
        {pts(player.sourcePoints.props)}
      </Typography>
    )}
  </Flex>
);

export const FantasyFootball = () => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [teamId, setTeamId] = useState<number | "">("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<PlayerProjection | null>(null);
  const request = useMemo<GetFantasyProjectionsRequest>(
    () => (teamId === "" ? {} : { teamId }),
    [teamId]
  );
  const [data, fetchData] = useFetchedResource(getFantasyProjections);
  const [ingestState, runIngest] = useFetchedResource(ingestFantasyData);
  useEffect(() => {
    fetchData(request);
  }, [fetchData, request]);
  useEffect(() => {
    if (ingestState.status === "SUCCESS") fetchData(request);
  }, [fetchData, ingestState.status, request]);

  return (
    <div className={classes.page}>
      <DocTitle title="Fantasy Football" />
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" style={{ flexGrow: 1 }}>
            Fantasy Football
          </Typography>
          <Button
            color="inherit"
            onClick={() => runIngest({})}
            disabled={ingestState.status === "PENDING"}
          >
            {ingestState.status === "PENDING" ? "Refreshing…" : "Refresh data"}
          </Button>
        </Toolbar>
      </AppBar>
      <div className={classes.body}>
        {anyIsError(ingestState) && (
          <Alert severity="error" style={{ marginBottom: 16 }}>
            Refresh failed: {errorMessage(ingestState)}. The 204 you may see in
            DevTools is the CORS preflight (OPTIONS), not the ingest result —
            look at the following POST.
          </Alert>
        )}
        {anyIsSuccess(ingestState) && ingestState.data && (
          <Alert severity="success" style={{ marginBottom: 16 }}>
            Ingested week {ingestState.data.week} ({ingestState.data.projectionsComputed}{" "}
            projections)
            {ingestState.data.credentialsExpired
              ? ". ESPN credentials expired — update ESPN_S2 / ESPN_SWID."
              : "."}
          </Alert>
        )}
        <FetchedComponent resource={data}>
          {(payload) => {
            const league = payload.league;
            const teams = league?.teams || [];
            const players = payload.projections || [];
            const filtered = players.filter((p) => {
              if (!query) return true;
              const q = query.toLowerCase();
              return (
                p.name.toLowerCase().includes(q) ||
                p.team.toLowerCase().includes(q) ||
                p.position.toLowerCase().includes(q)
              );
            });
            return (
              <Flex flexDirection="column">
                {payload.credentialsExpired && (
                  <Alert severity="error" style={{ marginBottom: 16 }}>
                    ESPN credentials expired. Update the ESPN_S2 / ESPN_SWID
                    secrets and refresh. Projections below may be stale.
                  </Alert>
                )}
                {payload.error && !payload.credentialsExpired && (
                  <Alert severity="warning" style={{ marginBottom: 16 }}>
                    {payload.error}
                  </Alert>
                )}
                <Paper className={classes.card}>
                  <Flex alignItems="center" flexWrap="wrap" style={{ gap: 16 }}>
                    <Typography variant="subtitle1">
                      Week {payload.week || "—"} · {payload.season || "—"}
                      {payload.ingestedAt
                        ? ` · updated ${new Date(
                            payload.ingestedAt
                          ).toLocaleString()}`
                        : ""}
                    </Typography>
                    <TextField
                      select
                      size="small"
                      label="Team"
                      value={teamId}
                      onChange={(e) =>
                        setTeamId(
                          e.target.value === "" ? "" : Number(e.target.value)
                        )
                      }
                      style={{ minWidth: 220 }}
                    >
                      <MenuItem value="">Viewer / default</MenuItem>
                      {teams.map((t) => (
                        <MenuItem key={t.teamId} value={t.teamId}>
                          {t.name}
                          {t.isViewer ? " (you)" : ""}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Typography variant="body2" color="textSecondary">
                      {players.length} projections · {league?.name || ""}
                    </Typography>
                  </Flex>
                </Paper>
                <Paper className={classes.card}>
                  <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                    <Tab label="Start / Sit" />
                    <Tab label="Acquire" />
                    <Tab label="Players" />
                  </Tabs>
                  {tab === 0 && (
                    <StartSitTable
                      rows={payload.startSit || []}
                      players={players}
                      onSelect={setSelected}
                    />
                  )}
                  {tab === 1 && (
                    <AcquireTable
                      rows={payload.waivers || []}
                      players={players}
                      onSelect={setSelected}
                    />
                  )}
                  {tab === 2 && (
                    <>
                      <TextField
                        size="small"
                        label="Search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        style={{ margin: "16px 0" }}
                      />
                      <PlayersTable
                        rows={filtered}
                        onSelect={setSelected}
                      />
                    </>
                  )}
                </Paper>
                <Dialog
                  open={Boolean(selected)}
                  onClose={() => setSelected(null)}
                  maxWidth="sm"
                  fullWidth
                >
                  {selected && (
                    <>
                      <DialogTitle>
                        {selected.name} · {selected.position} {selected.team}
                        {selected.opponent ? ` vs ${selected.opponent}` : ""}
                      </DialogTitle>
                      <DialogContent>
                        <Typography gutterBottom>
                          {pts(selected.projectedPoints)} pts (if active{" "}
                          {pts(selected.projectedIfActive)}) · floor{" "}
                          {pts(selected.floor)} · ceiling {pts(selected.ceiling)}
                        </Typography>
                        {selected.injuryStatus && (
                          <Chip
                            size="small"
                            label={selected.injuryStatus}
                            style={{ marginBottom: 8 }}
                          />
                        )}
                        {selected.injuryComment && (
                          <Typography variant="body2" paragraph>
                            {selected.injuryComment}
                          </Typography>
                        )}
                        <FactorList player={selected} />
                      </DialogContent>
                    </>
                  )}
                </Dialog>
              </Flex>
            );
          }}
        </FetchedComponent>
      </div>
    </div>
  );
};

const StartSitTable = ({
  rows,
  players,
  onSelect,
}: {
  rows: StartSitRecommendation[];
  players: PlayerProjection[];
  onSelect: (p: PlayerProjection) => void;
}) => {
  const classes = useStyles();
  const byId = Object.fromEntries(players.map((p) => [p.gsisId, p]));
  return (
    <div className={classes.tableWrap}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Player</TableCell>
            <TableCell>Pos</TableCell>
            <TableCell>Now</TableCell>
            <TableCell>Advice</TableCell>
            <TableCell align="right">Proj</TableCell>
            <TableCell align="right">Floor</TableCell>
            <TableCell align="right">Ceil</TableCell>
            <TableCell>Why</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.playerGsisId}
              hover
              onClick={() => {
                const p = byId[row.playerGsisId];
                if (p) onSelect(p);
              }}
              style={{ cursor: "pointer" }}
            >
              <TableCell>{row.name}</TableCell>
              <TableCell>{row.position}</TableCell>
              <TableCell>{slotLabel(row.currentSlot)}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={actionColor(row.action)}
                  label={row.action.toUpperCase()}
                />
                {row.closeCall && (
                  <Chip size="small" label="close" style={{ marginLeft: 6 }} />
                )}
                {row.gameLocked && (
                  <Chip size="small" label="played" style={{ marginLeft: 6 }} />
                )}
              </TableCell>
              <TableCell align="right">{pts(row.projectedPoints)}</TableCell>
              <TableCell align="right">{pts(row.floor)}</TableCell>
              <TableCell align="right">{pts(row.ceiling)}</TableCell>
              <TableCell>{row.reason}</TableCell>
            </TableRow>
          ))}
          {!rows.length && (
            <TableRow>
              <TableCell colSpan={8}>
                No roster loaded yet. Refresh after ESPN cookies are set.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

const AcquireTable = ({
  rows,
  players,
  onSelect,
}: {
  rows: WaiverRecommendation[];
  players: PlayerProjection[];
  onSelect: (p: PlayerProjection) => void;
}) => {
  const classes = useStyles();
  const byId = Object.fromEntries(players.map((p) => [p.gsisId, p]));
  return (
    <div className={classes.tableWrap}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Player</TableCell>
            <TableCell>Pos</TableCell>
            <TableCell>Opp</TableCell>
            <TableCell align="right">Proj</TableCell>
            <TableCell align="right">vs starter</TableCell>
            <TableCell align="right">Next weeks</TableCell>
            <TableCell align="right">Owned %</TableCell>
            <TableCell>Why</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.playerGsisId}
              hover
              onClick={() => {
                const p = byId[row.playerGsisId];
                if (p) onSelect(p);
              }}
              style={{ cursor: "pointer" }}
            >
              <TableCell>
                {row.name} {row.team}
              </TableCell>
              <TableCell>{row.position}</TableCell>
              <TableCell>{row.opponent}</TableCell>
              <TableCell align="right">{pts(row.projectedPoints)}</TableCell>
              <TableCell align="right">
                {row.pointsOverWorstStarter > 0 ? "+" : ""}
                {pts(row.pointsOverWorstStarter)}
              </TableCell>
              <TableCell align="right">{pts(row.upcomingWeeksValue)}</TableCell>
              <TableCell align="right">{pts(row.percentOwned)}</TableCell>
              <TableCell>{row.reason}</TableCell>
            </TableRow>
          ))}
          {!rows.length && (
            <TableRow>
              <TableCell colSpan={8}>No waiver upgrades found yet.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

const PlayersTable = ({
  rows,
  onSelect,
}: {
  rows: PlayerProjection[];
  onSelect: (p: PlayerProjection) => void;
}) => {
  const classes = useStyles();
  return (
    <div className={classes.tableWrap}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Player</TableCell>
            <TableCell>Pos</TableCell>
            <TableCell>Opp</TableCell>
            <TableCell>Injury</TableCell>
            <TableCell align="right">Proj</TableCell>
            <TableCell align="right">If active</TableCell>
            <TableCell align="right">Floor</TableCell>
            <TableCell align="right">Ceil</TableCell>
            <TableCell>Roster</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.slice(0, 250).map((row) => (
            <TableRow
              key={row.gsisId}
              hover
              onClick={() => onSelect(row)}
              style={{ cursor: "pointer" }}
            >
              <TableCell>
                {row.name} {row.team}
              </TableCell>
              <TableCell>{row.position}</TableCell>
              <TableCell>
                {row.isHome ? "vs" : "@"} {row.opponent}
              </TableCell>
              <TableCell>{row.injuryStatus || ""}</TableCell>
              <TableCell align="right">{pts(row.projectedPoints)}</TableCell>
              <TableCell align="right">{pts(row.projectedIfActive)}</TableCell>
              <TableCell align="right">{pts(row.floor)}</TableCell>
              <TableCell align="right">{pts(row.ceiling)}</TableCell>
              <TableCell>
                {row.onRosterOf || "FA"} {slotLabel(row.lineupSlotId)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
